import { isDefined } from "@clipboard-health/util-ts";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type ClipboardScorePolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ClipboardScoreActions } from "@src/appV2/redesign/ClipboardScore/utils/constants";

import { type GradientVariant, type ScoreChange } from "../types";
import { buildScoreChanges } from "./buildScoreChanges";
import { getGradientVariant } from "./getGradientVariant";
import { getScoreSubtitle } from "./getScoreSubtitle";

interface ScoreDataForEmergencyCreditsClockOutFlowParams {
  scoreLogs: ClipboardScoreLog[];
  currentScore: number;
  scoreChange: number;
  latenessMinutes: number;
  isSecondShiftInDouble: boolean;
  isFirstShiftInDouble: boolean;
  wasBookedWithShortLeadTime: boolean;
  newlyRestricted: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  formattedTargetArrivalTime?: string;
  policy?: ClipboardScorePolicy;
}

export interface EmergencyClockOutFlowShiftData {
  startScore: number;
  scoreChanges: ScoreChange[];
  subtitle: string;
  gradientVariant: GradientVariant;
  penaltyStartMinutes?: number;
}

/**
 * Composes buildScoreChanges, getScoreSubtitle, and getGradientVariant
 * into a single data object for the Emergency Credits clock-out flow.
 */
export function buildScoreDataForEmergencyCreditsClockOutFlow(
  params: ScoreDataForEmergencyCreditsClockOutFlowParams
): EmergencyClockOutFlowShiftData {
  const {
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
    policy,
  } = params;

  const latenessConfig = policy?.latenessConfig;

  const penaltyStartMinutes =
    latenessConfig && latenessConfig.length > 0
      ? Math.min(...latenessConfig.map((config) => config.minutesLateRange.startFrom))
      : undefined;

  const scoreChanges = buildScoreChanges(scoreLogs, latenessMinutes, latenessConfig);
  const startScore = currentScore - scoreChange;

  const wasLate = isDefined(latenessMinutes) && latenessMinutes > 0;
  const didLosePointsForTimeliness = scoreLogs.some(
    (log) =>
      log.action === ClipboardScoreActions.START_SHIFT_LATE &&
      (log.clipboardScoreAdjustment ?? 0) < 0
  );

  const subtitle = getScoreSubtitle({
    wasLate,
    latenessMinutes,
    didLosePointsForTimeliness,
    isArrivalTimeFeatureEnabled,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    targetArrivalTime: formattedTargetArrivalTime,
  });

  const gradientVariant = getGradientVariant({
    scoreChange,
    currentScore,
    latenessMinutes,
    newlyRestricted,
    isSecondShiftInDouble,
    wasBookedWithShortLeadTime,
    penaltyStartMinutes,
  });

  return {
    startScore,
    scoreChanges,
    subtitle,
    gradientVariant,
    penaltyStartMinutes,
  };
}
